import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'store';
import createBillingApi from 'api/billing';
import { saveAs } from 'file-saver';
import { showToast } from 'modules/app/toasterSlice';
import { currentUserSelector } from 'modules/user/userSlice';
import { appContextSelector } from 'modules/app/appSlice';

interface State {
  loading: boolean;
  reports: Bill[];
  reportsRequiresAction: Bill[];
}

const initialState: State = {
  loading: false,
  reports: [],
  reportsRequiresAction: [],
};

const slice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.loading = action.payload;
      return state;
    },
    setBills: (state: State, action: PayloadAction<Bill[]>): State => {
      state.reports = action.payload;
      return state;
    },
    setBillsRequiresAction: (state: State, action: PayloadAction<Bill[]>): State => {
      state.reportsRequiresAction = action.payload;
      return state;
    },
    moveToCompleted: (state: State, action: PayloadAction<string>): State => {
      const bill = state.reportsRequiresAction.find(r => r.id === action.payload);
      if (!bill) {
        return state;
      }
      state.reports = [...state.reports, bill];
      state.reportsRequiresAction = state.reportsRequiresAction.filter(r => r.id !== bill.id);
      return state;
    },
  },
});
const { setLoading, setBills, setBillsRequiresAction, moveToCompleted } = slice.actions;
export { setLoading, moveToCompleted };

export default slice.reducer;

export const loadBills = (): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentAccount = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const billingApi = createBillingApi(context);
  const isConnectContext = context === 'connect';

  try {
    let reports = await billingApi.getBills((isConnectContext ? currentAccount?.partnershipId : currentAccount?.customerId) ?? '', isConnectContext);
    const reportsRequiresAction = await billingApi.getBillsRequiresAction(
      (isConnectContext ? currentAccount?.partnershipId : currentAccount?.customerId) ?? '',
      isConnectContext,
    );
    reports = reports.filter(r => reportsRequiresAction.findIndex(rr => rr.id === r.id) === -1);
    dispatch(setBills(reports));
    dispatch(setBillsRequiresAction(reportsRequiresAction));
    dispatch(setLoading(false));
  } catch (ex) {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
    dispatch(setLoading(false));
  }
};

export const downloadBill = (id: string, reportId: string, fileName: string): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const isConnectContext = context === 'connect';
  const billingApi = createBillingApi(context);
  try {
    const blob = await billingApi.downloadBill(id, reportId, isConnectContext);
    saveAs(blob, `${fileName}.pdf`);
  } catch {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};
